import { Navigate, useNavigate, useParams } from "react-router-dom";
import { TicketDetails } from "../../components/tickets/TicketDetails";
import { PageHeader } from "../../components";
import { useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { Button, Spinner } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { CloseWithReasonsAction } from "../../components/tickets/cells/CloseWithReasonsAction";
import { TicketCheck, X } from "lucide-react";
import { canCloseTicket } from "../../components/tickets/utils";
import { useMemo } from "react";
import { formatDateTime } from "@tellescope/utils";

export const TicketDetailsPage = () => {
  const { t } = useTranslation("tickets");
  const params = useParams();
  const ticketId = params.ticketId;
  const ticketQuery = useTellescopeQuery({
    model: "tickets",
    method: "getOne",
    request: ticketId ?? "",
    enabled: !!ticketId,
  });
  const closeTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "close_ticket",
  });
  const navigate = useNavigate();
  const canClose = useMemo(() => (ticketQuery.data ? canCloseTicket(ticketQuery.data) : false), [ticketQuery.data]);

  if (!ticketId) {
    return <Navigate replace to="/tickets" />;
  }

  if (ticketQuery.isError) {
    return (
      <div className="h-full w-full flex items-center justify-center text-destructive">{t("Error loading ticket")}</div>
    );
  }

  if (ticketQuery.isLoading || !ticketQuery.data) {
    return (
      <div className="min-h-svh w-full flex flex-col items-center justify-center gap-4">
        <Spinner />
        <p className="text-muted-foreground">{t("Loading ticket...")}</p>
      </div>
    );
  }

  return (
    <div className="max-h-svh overflow-hidden flex flex-col">
      <PageHeader>
        <div className="w-full flex justify-between">
          <h1 className="text-xl font-bold truncate" data-testid="ticket-details-title">
            {ticketQuery.data.title}
          </h1>
          <div className="flex gap-1 items-center">
            {ticketQuery.data.closeReasons?.length && canClose ? (
              <CloseWithReasonsAction
                disabled={!!ticketQuery.data.closedAt}
                reasons={ticketQuery.data.closeReasons}
                onSelectReason={(reason) => {
                  closeTicketMutation.mutateAsync({
                    ticketId,
                    closedForReason: reason,
                  });
                }}
                Icon={TicketCheck}
              />
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                data-testid="close-ticket-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  closeTicketMutation.mutateAsync({
                    ticketId,
                  });
                }}
                disabled={!!ticketQuery.data.closedAt}
                hoverLabel={
                  ticketQuery.data.closedAt
                    ? t("Closed: {{date}}", {
                        date: formatDateTime(ticketQuery.data.closedAt, "MMM D, YYYY at hh:mm a", {
                          unit: "days",
                          amount: 7,
                          direction: "both",
                        }),
                      })
                    : canClose
                    ? t("Mark Complete")
                    : t("Complete Actions before Closing")
                }
              >
                <TicketCheck className="h-4 w-4" />
              </Button>
            )}

            <Button variant="ghost" size="sm-icon" onClick={() => navigate(-1)}>
              <X />
            </Button>
          </div>
        </div>
      </PageHeader>
      <TicketDetails ticketId={ticketId} ticket={ticketQuery.data} />
    </div>
  );
};
